import { useCallback, useState } from 'react';

interface UseLocalStorageI<T> {
  storedValue: T;
  setValue: (value: T | ((val: T) => T)) => void;
  deleteValue: (key: string) => void;
}

export const useLocalStorage = <T>(key: string, initialValue: T): UseLocalStorageI<T> => {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once

  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      // Get from local storage by key
      const item = window.localStorage.getItem(key);
      // Parse stored json or if none return initialValue
      return item ? JSON.parse(item) : initialValue;
    } catch {
      // If error also returns initialValuex
      // console.log(error);
      return initialValue;
    }
  });

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = useCallback(
    (value: T | ((val: T) => T)) => {
      // Compute from the latest state so callbacks stay stable without closing
      // over a stale storedValue.
      setStoredValue((currentValue) => {
        const valueToStore = value instanceof Function ? value(currentValue) : value;
        try {
          window.localStorage.setItem(key, JSON.stringify(valueToStore));
        } catch {
          // localStorage can be unavailable in private/restricted browser contexts.
        }
        return valueToStore;
      });
    },
    [key]
  );

  const deleteValue = useCallback((storageKey: string) => {
    window.localStorage.removeItem(storageKey);
  }, []);
  return { storedValue, setValue, deleteValue };
};
